import { Download, Search } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Modal,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";
import Cookies from "js-cookie";
import get from "lodash/get";
import { useCallback, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useNotification from "../../hooks/useNotification";
import purchaseService from "../../services/purchase";
import config from "../../utils/config";
import queryDefault from "../../utils/queryDefault";

const CustomNoRowOverlay = () => (
  <Box sx={{ pt: 5 }}>
    <Typography variant="body1" textAlign="center" fontWeight="600">
      No purchases found
    </Typography>
  </Box>
);

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
  maxHeight: "80vh",
  overflow: "scroll"
};

export default function PurchaseRequest() {
  const navigate = useNavigate();
  const [pageSize, setPageSize] = useState<number>(10);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [idForDecline, setIdForDecline] = useState<number>(0);
  const [declineReason, setDeclineReason] = useState<string>("");
  const user = JSON.parse(Cookies.get("USER") || "{ }");
  const [options, setOptions] = useState<any>({
    keyword: "",
    status: "draft",
    startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
  });

  const notification = useNotification();

  const listPurchases = useQuery(
    ["purchaseRequest", options],
    ({ queryKey }) => purchaseService.getAllPurchases(queryKey[1]),
    {
      ...queryDefault,
    }
  );

  const downloadPurchase = useMutation(purchaseService.downloadPurchase, {
    onSuccess: () => {
      notification.onOpen({
        message: "Purchase document was downloaded successfully!",
        type: "success",
        position: "top",
      });
    },
    onError: () => {
      notification.onOpen({
        message: "Failed to download purchase",
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const exportPurchases = useMutation(purchaseService.exportPurchases, {
    onSuccess: () => {
      notification.onOpen({
        message: "Purchases exported successfully!",
        type: "success",
        position: "top",
      });
    },
    onError: () => {
      notification.onOpen({
        message: "Failed to export purchases",
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const handleDownload = (purchaseId: number) => {
    downloadPurchase.mutate(purchaseId);
  };

  const handleExportPurchases = () => {
    const values = form.getValues();

    exportPurchases.mutate({
      keyword: values.keyword,
      status: options.status,
      startDate: values.startDate,
      endDate: values.endDate,
    });
  };

  const form = useForm({
    defaultValues: {
      keyword: "",
      startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
      endDate: dayjs().format('YYYY-MM-DD'),
    },
  });

  const onHandleSearch = useCallback((values: { keyword: string; startDate: string; endDate: string }) => {
    setOptions((prev: any) => ({
      ...prev,
      keyword: values.keyword,
      startDate: values.startDate,
      endDate: values.endDate,
      page_number: 0,
    }));
  }, []);

  const handleAcceptPurchase = (purchaseId: number) => {
    if (window.confirm("Are you sure you want to accept this purchase?")) {
      purchaseService.acceptPurchase({ id: purchaseId }).then(() => {
        notification.onOpen({
          message: "Purchase accepted successfully!",
          type: "success",
          position: "top",
        });
      }).catch(() => {
        notification.onOpen({
          message: "Failed to accept purchase",
          type: "error",
          position: "top",
        });
      });
      listPurchases.refetch();
    }
  };

  const handleDeclineWithReason = (purchaseId: number, declineReason: string) => {
    if (window.confirm("Are you sure you want to decline this purchase?")) {
      purchaseService.declinePurchase({ id: purchaseId, decline_reason: declineReason }).then(() => {
        notification.onOpen({
          message: "Purchase declined successfully!",
          type: "success",
          position: "top",
        });
        listPurchases.refetch();
      }).catch(() => {
        notification.onOpen({
          message: "Failed to decline purchase",
          type: "error",
          position: "top",
        });
      });
      setIdForDecline(0);
      setDeclineReason("");
      setOpenModal(false);
    }
  };

  return (
    <MainLayout>
      <Helmet>
        <title>Purchase Request</title>
      </Helmet>

      <Breadcrumb
        label="Purchase Request"
        breadcrumbs={[{ label: "Dashboard", href: "/app" }]}
      />

      {/* Only admin can access this page */}
      <ProtectedPermissionPage acceptPermissions={[1, 2]}>
        <Box sx={{ mt: 3 }}>
          <Stack direction="row" justifyContent="space-between" spacing={2} useFlexGap flexWrap="wrap">
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Controller
                name="keyword"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Search purchases..."
                    {...field}
                    {...config.onEnter(() => {
                      form.handleSubmit(onHandleSearch)();
                    })}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment
                          position="end"
                          onClick={form.handleSubmit(onHandleSearch)}
                        >
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
              <Controller
                name="startDate"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="date"
                    variant="outlined"
                    size="small"
                    label="Start Date"
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => {
                      field.onChange(e);
                      setOptions((prev: any) => ({ ...prev, startDate: e.target.value, page_number: 0 }));
                    }}
                  />
                )}
              />
              <Controller
                name="endDate"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="date"
                    variant="outlined"
                    size="small"
                    label="End Date"
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => {
                      field.onChange(e);
                      setOptions((prev: any) => ({ ...prev, endDate: e.target.value, page_number: 0 }));
                    }}
                  />
                )}
              />
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                component={RouterLink}
                to="/app/purchase/create-purchase"
              >
                Create Purchase
              </Button>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={handleExportPurchases}
                disabled={exportPurchases.isLoading}
              >
                Export Purchases
              </Button>
            </Stack>
          </Stack>
        </Box>

        <Box sx={{ mt: 2 }}>
          <DataGrid
            autoHeight
            loading={listPurchases.isLoading}
            getRowId={(record) => record.id}
            rows={get(listPurchases, "data.data", [])}
            pageSize={pageSize}
            rowsPerPageOptions={[10, 20, 30]}
            pagination
            disableSelectionOnClick
            onPageChange={(newPage) => {
              setOptions((prev: any) => ({ ...prev, page_number: newPage }));
            }}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            components={{
              NoRowsOverlay: CustomNoRowOverlay,
            }}
            columns={[
              {
                field: "purchase_no",
                headerName: "Purchase No",
                flex: 1,
                minWidth: 150,
                renderCell: (params) => (
                  <Button
                    variant="text"
                    onClick={() => navigate(`/app/purchase/detail/${params.row.id}`)}
                    sx={{ textTransform: 'none', justifyContent: 'flex-start' }}
                  >
                    {params.value}
                  </Button>
                ),
              },
              {
                field: "usage_date",
                headerName: "Usage Date",
                flex: 1,
                minWidth: 120,
                renderCell: (params) => (
                  <Typography>
                    {params.value ? dayjs(params.value).format("DD/MM/YYYY") : "N/A"}
                  </Typography>
                ),
              },
              {
                field: "request_date",
                headerName: "Request Date",
                flex: 1,
                minWidth: 120,
                renderCell: (params) => (
                  <Typography>
                    {params.value ? dayjs(params.value).format("DD/MM/YYYY") : "N/A"}
                  </Typography>
                ),
              },
              {
                field: "created_by_name",
                headerName: "Created By",
                flex: 1,
                minWidth: 120,
                renderCell: (params) => (
                  <Typography>
                    {params.value}
                  </Typography>
                ),
              },
              {
                field: "id",
                headerName: "Actions",
                sortable: false,
                flex: 1,
                minWidth: 150,
                renderCell: (params) => {
                  return (
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Download purchase">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleDownload(params.value)}
                        >
                          <Download />
                        </IconButton>
                      </Tooltip>

                      {user.role_id === 1 && (
                        <>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleAcceptPurchase(params.value)}
                          >
                            Accept
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setIdForDecline(params.value);
                              setDeclineReason("");
                              setOpenModal(true);
                            }}
                          >
                            Decline
                          </Button>
                        </>
                      )}
                    </Stack>
                  );
                },
              },
            ]}
            sx={{
              ".MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
              },
              ".MuiDataGrid-columnHeaderTitleContainer": {
                backgroundColor: "#303c40",
                color: "white",
              },
              ".MuiDataGrid-columnHeader": {
                backgroundColor: "#303c40",
                color: "white",
              },
              ".MuiDataGrid-columnSeparator": {
                backgroundColor: "#303c40",
                color: "white",
              },
            }}
          />
        </Box>
        <Modal
          open={openModal}
          onClose={() => setOpenModal(false)}
          aria-labelledby="decline-reason-modal-title"
          aria-describedby="decline-reason-modal-description"
        >
          <Box sx={style}>
            <Typography variant="h6" gutterBottom>
              Enter Decline Reason
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="Please provide a reason for declining this purchase request..."
            />
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleDeclineWithReason(idForDecline, declineReason)}
              >
                Submit
              </Button>
            </Box>
          </Box>

        </Modal>
      </ProtectedPermissionPage>
    </MainLayout >
  );
}